// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { pageSelected } from 'actions/pageView';

import { useFocusManager } from 'hooks/focus';
import { useRestoration } from 'hooks/restoration';
import useTracking from 'hooks/analytics';

import {
  getPageValues,
  createBreadCrumb,
} from 'support/analytics/analyticsHelper';

import SettingsRow from 'components/pages/home/SettingsRow';
import LeftNav from 'components/navigation/LeftNav';
import { FocusSettingsProvider } from 'components/focus/FocusSettingsContext';
import Footer from 'components/common/Footer';

import { FocusGroup } from 'components/focus';
import WithLoading from 'components/common/WithLoading';

import styles from './SettingsPage.module.scss';

const RestorationStatus = {
  RESTORED: 'restored',
  STARTED: 'started',
  UNRESTORED: 'unrestored',
};

const trackSettingsPage = (tracker, { content, pageType, historyStack }) => {
  const pageData = {
    pageType,
    ...getPageValues(content),
    breadCrumb: createBreadCrumb(historyStack),
  };
  tracker(pageData);
};

const useSettingsPageRestoration = isReady => {
  const { restorationState, setRestorationState } = useRestoration('Settings');
  const focusManager = useFocusManager();

  const restoredFocusId = restorationState?.focusId;

  const [restorationStatus, setRestorationStatus] = useState(
    RestorationStatus.STARTED,
  );

  useEffect(() => {
    if (isReady && restorationStatus === RestorationStatus.STARTED) {
      if (
        restoredFocusId &&
        focusManager.getFocusableElement(restoredFocusId)
      ) {
        focusManager.setCurrentFocus(restoredFocusId);
        setRestorationStatus(RestorationStatus.RESTORED);
      } else {
        setRestorationStatus(RestorationStatus.UNRESTORED);
      }
    }
  }, [focusManager, isReady, restoredFocusId, restorationStatus]);

  // update here
  useEffect(() => {
    const onFocusChangedListener = () => {
      if (focusManager.isFocusAncestor('settings-focusable-content')) {
        setRestorationState({
          ...restorationState,
          focusId: focusManager.currentFocusId,
        });
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, restorationState, setRestorationState]);

  return [restorationStatus, restoredFocusId];
};

const SettingsPage = ({ pageName, pageType }) => {
  const dispatch = useDispatch();

  const { contentId } = useParams();
  const loading = false;
  const focusManager = useFocusManager();
  const { trackPage: tracker } = useTracking();
  const history = useHistory();

  const footerRef = useRef();

  // Handles the initial focus setting logic
  const hasFocusBeenInitialized = useRef(false);
  const [restoredFocus] = useSettingsPageRestoration(!loading);

  useEffect(() => {
    dispatch(pageSelected('settings', null));
  }, [dispatch]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.UNRESTORED) {
      if (!hasFocusBeenInitialized.current) {
        focusManager.setCurrentFocus(
          `${pageName.toLowerCase()}-focusable-content`,
        );
        hasFocusBeenInitialized.current = true;
      }
    }
  }, [focusManager, restoredFocus, pageName]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.RESTORED) {
      hasFocusBeenInitialized.current = true;
    }
  }, [restoredFocus]);

  useEffect(() => {
    if (!loading && hasFocusBeenInitialized.current) {
      focusManager.setCurrentFocus(
        `${pageName.toLowerCase()}-focusable-content`,
      );
    }
  }, [focusManager, loading, pageName]);

  useEffect(() => {
    trackSettingsPage(tracker, {
      content: contentId,
      pageType,
      historyStack: history.entries,
    });
  }, [tracker, contentId, pageType, history]);

  return (
    <WithLoading loading={loading}>
      <LeftNav focusId="left-nav-focusable-content" />
      <FocusSettingsProvider>
        {!loading && (
          <FocusGroup
            focusId={`${pageName.toLowerCase()}-focusable-content`}
            className="w-100 h-100 position-relative"
          >
            <div
              className={classNames(
                'overflow-hidden',
                'w-100 h-100',
                styles.backgroundColor,
                styles.margin,
                styles.SettingsPage,
              )}
            >
              <span className={styles.headerTitle}>Settings</span>

              <SettingsRow focusIdPrefix="firstrow-" />
            </div>
          </FocusGroup>
        )}
        <Footer ref={footerRef} className={classNames(styles.SettingsFooter)} />
      </FocusSettingsProvider>
    </WithLoading>
  );
};

SettingsPage.propTypes = {
  pageName: PropTypes.string,
  pageType: PropTypes.string,
};

SettingsPage.defaultProps = {
  pageName: null,
  pageType: null,
};

export default SettingsPage;
